import styled from '@emotion/styled';
import { type ComponentType, type InputHTMLAttributes, useEffect, useState } from 'react';

import { filterNumericInput } from '@/utils/filterNumberInput';
import { FORM_COLOR, StyledInput, TagOption, TagSelector } from '@/pages/Seminar/components/form/formStyles';

/** 연차 입력 상한 — 서버(MemberCareerYears.MAX_YEARS)·급여 실태조사와 같다 */
export const MAX_CAREER_YEARS = 60;

/** 화면 값('' 미입력, '0' 신입, 'N' 경력 N년) → 숫자. 없거나 범위 밖이면 null */
export const careerYearsOf = (value: string): number | null => {
    if (value.trim() === '') return null;
    const years: number = Number(value);
    return Number.isInteger(years) && years >= 0 && years <= MAX_CAREER_YEARS ? years : null;
};

/** 필수 검사 — 통과면 null, 아니면 칸 아래 문구 */
export const careerYearsError = (value: string, careerChosen: boolean): string | null => {
    if (value.trim() === '') return careerChosen ? '경력 연수를 입력해 주세요.' : '연차를 선택해 주세요.';
    const years: number | null = careerYearsOf(value);
    if (years === null) return `연차는 0~${MAX_CAREER_YEARS}년 사이로 입력해 주세요.`;
    if (careerChosen && years === 0) return '경력은 1년 이상으로 입력해 주세요. 1년 미만이면 신입을 골라 주세요.';
    return null;
};

/** 서버 값(회원 정보 연차) → 화면 값 */
export const careerYearsText = (years: number | null | undefined): string => (years === null || years === undefined ? '' : String(years));

/** 화면 글 — 신입 / N년 (서버 MemberCareerYears.label 과 같다) */
export const careerYearsLabel = (years: number | null | undefined): string =>
    years === null || years === undefined ? '' : years <= 0 ? '신입' : `${years}년`;

type InputProps = InputHTMLAttributes<HTMLInputElement> & { hasError?: boolean };

/**
 * 회원 연차 입력 — [신입][경력] 알약 칩 + 경력이면 "N 년" 숫자 칸(UI 가이드 3장).
 * 연차는 회원 정보 한 곳에 저장한다(가입·회원 정보 수정·이력서가 이 부품 하나를 쓴다). 이력서 경력 기간으로 계산하지 않는다.
 *
 * @param value '' 미입력 / '0' 신입 / 'N' 경력 N년
 * @param onChange 화면 값과 [경력]을 골랐는지
 * @param input 숫자 칸 부품 — 가입은 밑줄형(SignupInput), 그 밖은 상자형(StyledInput)
 */
export default function CareerYearsField({
    value,
    onChange,
    error,
    input: Input = StyledInput as ComponentType<InputProps>,
    id = 'careerYears',
}: {
    value: string;
    onChange: (value: string, careerChosen: boolean) => void;
    error?: string | null;
    input?: ComponentType<InputProps>;
    id?: string;
}) {
    const years: number | null = careerYearsOf(value);
    // [경력]을 눌렀지만 아직 숫자를 안 넣은 상태도 경력으로 본다
    const [careerChosen, setCareerChosen] = useState<boolean>(years !== null && years > 0);
    useEffect(() => {
        // 바깥에서 경력 값을 불러오면 [경력] 칩을 켠다(경력 칸에 0 을 치는 중에는 칩을 바꾸지 않는다)
        if (years !== null && years > 0) setCareerChosen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [years]);
    const newcomer: boolean = !careerChosen && years === 0;

    return (
        <Wrap data-field="careerYears">
            <TagSelector role="group" aria-label="연차">
                <TagOption
                    type="button"
                    selected={newcomer}
                    aria-pressed={newcomer}
                    onClick={() => {
                        setCareerChosen(false);
                        onChange('0', false);
                    }}
                >
                    신입
                </TagOption>
                <TagOption
                    type="button"
                    selected={careerChosen}
                    aria-pressed={careerChosen}
                    onClick={() => {
                        setCareerChosen(true);
                        onChange(years !== null && years > 0 ? value : '', true);
                    }}
                >
                    경력
                </TagOption>
            </TagSelector>
            {careerChosen && (
                <Unit>
                    <Input
                        id={id}
                        name="careerYears"
                        inputMode="numeric"
                        aria-label="경력 연수(년)"
                        placeholder="경력 연수"
                        value={value}
                        hasError={!!error}
                        aria-invalid={!!error}
                        onChange={(e) => onChange(filterNumericInput(e.target.value).slice(0, 2), true)}
                    />
                    <em>년</em>
                </Unit>
            )}
            {error && <ErrorText role="alert">{error}</ErrorText>}
        </Wrap>
    );
}

const Wrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

/** 숫자 칸 오른쪽 "년" — formStyles UnitInput 과 같은 자리 */
const Unit = styled.div`
    position: relative;
    min-width: 0;
    max-width: 200px;

    input {
        padding-right: 44px;
    }

    > em {
        position: absolute;
        right: 14px;
        top: 50%;
        transform: translateY(-50%);
        font-style: normal;
        font-size: 15px;
        font-weight: 700;
        color: ${FORM_COLOR.sub};
        pointer-events: none;
    }
`;

const ErrorText = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
    color: ${FORM_COLOR.error};
`;
